import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Calendar, Gamepad2, Star, Crown, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface CommunityCardData {
  id: string;
  name: string;
  slug?: string;
  description: string;
  cover_url?: string | null;
  cover_image_url?: string | null;
  member_count: number;
  is_featured?: boolean;
  created_at?: string;
  game: {
    name: string;
    icon?: string;
  };
  mentor: {
    profile: {
      nickname: string;
      image_url?: string;
    };
  };
  is_owner?: boolean; // Para mentores - indica se é dono da comunidade
}

interface CommunityCardProps {
  community: CommunityCardData;
  showOwnerBadge?: boolean;
  showMentorInfo?: boolean;
  onVisit?: (communityId: string) => void;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  community,
  showOwnerBadge = false,
  showMentorInfo = true,
  onVisit
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Usar slug se disponível, senão usar ID como fallback
  const communityUrl = community.slug 
    ? `/comunidades/${community.slug}` 
    : `/comunidade/${community.id}`;
  
  // Priorizar cover_url, depois cover_image_url
  const coverImage = community.cover_url || community.cover_image_url;
  
  const handleVisit = () => {
    console.log("🔗 Navegando para comunidade:", {
      name: community.name,
      slug: community.slug,
      url: communityUrl
    });
    
    if (onVisit) {
      onVisit(community.id);
    } else {
      // Navegar para a página da comunidade usando slug
      window.location.href = communityUrl;
    }
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Função para truncar a descrição
  const getShortDescription = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit", 
      year: "numeric"
    });
  };

  return (
    <Card className="bg-[#1A2233] border-[#2A3441] text-white hover:border-[#00B6F0] transition-all duration-300 overflow-hidden group h-full">
      {/* Header com imagem de capa - Layout igual aos cards das mentorias */}
      <div className="relative h-48 overflow-hidden">
        {coverImage ? (
          <img 
            src={coverImage} 
            alt={community.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              console.log("❌ Erro ao carregar imagem:", coverImage);
              // Fallback para imagem padrão
              const target = e.target as HTMLImageElement;
              target.src = "https://via.placeholder.com/400x200/1A2233/00B6F0?text=Comunidade";
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#2A3441] to-[#1A2233] flex items-center justify-center">
            <Gamepad2 className="w-16 h-16 text-[#00B6F0] opacity-50" />
          </div>
        )}
        
        {/* Gradiente overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A2233] via-transparent to-transparent" />
        
        {/* Badge do jogo no canto superior esquerdo */}
        <div className="absolute top-3 left-3">
          <Badge className="bg-[#00B6F0]/90 text-black border-[#00A3D9]/50 backdrop-blur-sm font-medium">
            {community.game.name}
          </Badge>
        </div>
        
        {/* Badges no canto superior direito */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {community.is_featured && (
            <Badge className="bg-yellow-500/90 text-yellow-100 border-yellow-400/50 backdrop-blur-sm">
              <Star className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
          {showOwnerBadge && community.is_owner && (
            <Badge className="bg-purple-500/90 text-purple-100 border-purple-400/50 backdrop-blur-sm">
              <Crown className="w-3 h-3 mr-1" />
              Minha
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-4 space-y-3 flex flex-col h-auto">
        {/* Título da comunidade */}
        <div>
          <CardTitle className="text-lg font-bold text-white group-hover:text-[#00B6F0] transition-colors line-clamp-1">
            {community.name}
          </CardTitle>
        </div>

        {/* Descrição */}
        <div className="min-h-[2.5rem] flex-1">
          <p className="text-gray-400 text-sm">
            {isExpanded ? community.description : getShortDescription(community.description)}
          </p>
          {community.description.length > 80 && (
            <button
              onClick={toggleExpanded}
              className="text-[#00B6F0] text-xs hover:text-[#00C7FF] transition-colors mt-1 flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  Ver menos <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  Ver mais <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Informações do mentor - Layout igual aos cards das mentorias */}
        {showMentorInfo && (
          <div className="flex items-center gap-3 py-2">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              {community.mentor.profile.image_url ? (
                <img 
                  src={community.mentor.profile.image_url}
                  alt={community.mentor.profile.nickname}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback para avatar com inicial se a imagem falhar
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="w-full h-full bg-[#00B6F0] flex items-center justify-center">
                          <span class="text-black font-semibold text-sm">
                            ${community.mentor.profile.nickname.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      `;
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full bg-[#00B6F0] flex items-center justify-center">
                  <span className="text-black font-semibold text-sm">
                    {community.mentor.profile.nickname.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {community.mentor.profile.nickname}
              </p>
              <p className="text-gray-400 text-xs">Mentor</p>
            </div>
          </div>
        )}

        {/* Estatísticas na parte inferior */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-700">
          <div className="flex items-center gap-2 text-gray-300">
            <Users className="w-4 h-4" />
            <span className="text-sm">{community.member_count} membros</span>
          </div>
          
          {community.created_at && (
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span className="text-xs">{formatDate(community.created_at)}</span>
            </div>
          )}
        </div>

        {/* Botão de ação */}
        <Button 
          onClick={handleVisit}
          className="w-full bg-[#00B6F0] text-black hover:bg-[#00A3D9] font-medium transition-colors mt-auto"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Visitar Comunidade
        </Button>
      </CardContent>
    </Card>
  );
};

export default CommunityCard; 